import {
  Alert,
  Box, CircularProgress, FormControl, FormControlLabel,
  Grid,
  Radio, RadioGroup, Snackbar, Typography, useTheme,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ChangeEvent, useState } from 'react';
import { unwrapResult } from '@reduxjs/toolkit';
import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingStatus } from '../../../common/enums';
import { updateMemberToPartner } from '../../../store/thunks/dashboardThunk';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { selectMemberSession, updateNavigationFromOnboarding } from '../../../store/slices/onboardingSlice';
import { TestIds } from '../../../assets/testIds';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import { useOnboardingRedirectPath } from '../../../common/useOnboardingRedirectPath';
import OptimusButton from '../../../common/library/Button/Button';
import { isUserInitiatedHomeDepotEnrollment } from '../../../common/homeDepotEnrollmentIntent';

interface IFormInputs {
  shoppingStatus: ShoppingStatus | null;
}

interface OnboardHDShopQuestionProps {
  setShowHDShopQuestion: (value: boolean) => void,
}
export function OnboardHDShopQuestionV2({ setShowHDShopQuestion }: OnboardHDShopQuestionProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { memberId } = useAppSelector(selectMemberSession);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<ShoppingStatus | null>(null);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { trackPartnerEnrollment, trackHomeDepotEnrollmentStart } = useCustomEventTracking();
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const {
    register, handleSubmit, control, formState,
  } = useForm<IFormInputs>({ mode: 'onChange' });

  // get redirect path on optimizely decision
  const redirectPath = useOnboardingRedirectPath({
    redirectToPartnersForOnboardingFlowTestOff: true,
  });

  // Handle the radio button change
  const handleRadioChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(e.target.value as ShoppingStatus);
  };

  const onSubmit = (data: IFormInputs) => {
    if (data.shoppingStatus) {
      setLoading(true);
      dispatch(updateMemberToPartner({
        memberId,
        partnerId: process.env.REACT_APP_HOME_DEPOT_PARTNER_ID || '',
        data: { shoppingStatus: data.shoppingStatus },
      }))
        .then(unwrapResult)
        .then(() => {
          setOpenErrorSnackbar(false);

          // fire shopping status event
          trackPartnerEnrollment('Submit', 'HomeDepotShoppingStatus', {
            shoppingStatus: data.shoppingStatus || 'unknown',
          });

          if (data.shoppingStatus === ShoppingStatus.NotShopping) {
            const userInitiatedHdEnrollment = isUserInitiatedHomeDepotEnrollment({
              state: location.state,
              search: location.search,
            });

            if (!userInitiatedHdEnrollment) {
              dispatch(updateNavigationFromOnboarding(true));
            }
            // navigate to content home page or rewards page
            navigate(redirectPath, {
              state: { fromOnboarding: true },
            });
          } else {
            // show the hd setup instructions
            setShowHDShopQuestion(false);
            trackHomeDepotEnrollmentStart();
          }
        })
        .catch((error: unknown) => {
          setOpenErrorSnackbar(true);
          setLoading(false);
        });
    }
  };

  const handleErrorClose = () => {
    setOpenErrorSnackbar(false);
  };

  return (
    <Box
    >
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleErrorClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl component="fieldset" sx={{ width: '100%' }}>
          <Controller
            name="shoppingStatus"
            control={control}
            defaultValue={null}
            rules={{
              required: 'Please select an option', // Validation message
            }}
            render={({ field: { onChange, value } }) => (
              <RadioGroup
                aria-label="shopping status"
                row
                value={value}
                onChange={(e) => {
                  onChange(e);
                  handleRadioChange(e); // Call the handler on change
                }}
              >
                <Grid container spacing={1}>
                  {[
                    {
                      value: ShoppingStatus.Shopping,
                      label: 'Yes',
                      id: TestIds.onboarding.homedepotShopYesBtn,
                      isDisabled: loading,
                    },
                    {
                      value: ShoppingStatus.NotShopping,
                      label: 'No',
                      id: TestIds.onboarding.homedepotShopNoBtn,
                      isDisabled: loading,
                    },
                  ].map((opt) => {
                    const isSelected = value === opt.value;
                    return (<Grid item xs={6} key={opt.value}>
                      <Box
                        sx={{
                          border: `1px solid ${isSelected ?
                            theme.palette.brand.primary :
                            theme.palette.grey[300]}`,
                          borderRadius: theme.spacing(1.25),
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <FormControlLabel
                          key={opt.id}
                          id={opt.id}
                          data-testid={opt.id}
                          value={opt.value}
                          control={
                            <Radio
                              sx={{
                                '&.Mui-checked': {
                                  color: theme.palette.brand.primary,
                                },
                                '&:hover': {
                                  backgroundColor: theme.palette.brand.surface,
                                },
                                padding: theme.spacing(1.25),
                              }}
                            />}
                          label={opt.label}
                          sx={{ margin: 0, width: '100%' }}
                          {...register('shoppingStatus')}
                        />
                      </Box>
                    </Grid>);
                  })}
                </Grid>
              </RadioGroup>
            )} />
        </FormControl>
        <OptimusButton
          size={'m'}
          type={'submit'}
          buttonText={loading ?
            <CircularProgress color="inherit" size={24} /> :
            <Typography component='span' data-testid={TestIds.onboarding.homedepotShopNextBtn} sx={{ fontWeight: 600 }}>
              {selectedOption === ShoppingStatus.NotShopping ?
                t('button.completeHdWebSetup') :
                t('button.next')
              }
            </Typography>}
          disabled={!formState.isValid || loading}
          sx={{
            mt: theme.spacing(3),
          }}
        />
      </form>
    </Box>
  );
}
